// PDF generation for the workout sheet. Produces a real text-based PDF
// (selectable, scalable) sized to a US Letter page. Auto-fits the content
// onto at most TWO pages by:
//   1. Trying a "preferred" font size first.
//   2. Measuring the layout. If anything would overflow page 2, retrying
//      with a smaller size step. Floors at MIN_BODY_PT — past that we
//      truncate with a "(continued in next class plan)" note rather than
//      let the user lose information silently.
//
// Output: a Blob URL opened in a new tab. We never call window.print().

import { jsPDF } from "jspdf";

export interface PdfExercise {
  title: string;
  body: string[];
}

export interface PdfArgs {
  date: string;            // formatted "MM/DD/YYYY"
  generatedOn: string;     // formatted "MM/DD/YYYY"
  exercises: PdfExercise[];
}

// Page geometry — US Letter, in inches.
const PAGE_W = 8.5;
const PAGE_H = 11;
const MARGIN = 0.45;
const COL_GAP = 0.25;
const USABLE_W = PAGE_W - 2 * MARGIN;
const COL_W = (USABLE_W - COL_GAP) / 2;

// Header & footer reserved space (inches from the page edge inward).
const HEADER_H = 0.55;
const FOOTER_H = 0.25;

const MAX_PAGES = 2;

// Font size candidates (pt). Try in order — first one that fits 2 pages wins.
const FONT_SIZES = [9.5, 9, 8.5, 8, 7.5, 7, 6.5];
const MIN_BODY_PT = FONT_SIZES[FONT_SIZES.length - 1];

// Brand accent (indigo) for the PDF eyebrow.
const ACCENT = [79, 70, 229] as const;

// jsPDF's default Helvetica font uses WinAnsiEncoding (CP1252). Characters
// outside that set render as garbled zero-width glyphs. Map the ones that
// actually appear in our choreography source to ASCII equivalents before
// drawing. Most common offender: ⁄ (fraction slash) inside "3⁄4".
const CHAR_MAP: Record<string, string> = {
  "⁄": "/",   // ⁄ fraction slash → /
  " ": " ",   // thin space
  " ": " ",   // hair space
  "​": "",    // zero-width space
  " ": " ",   // narrow no-break space
  " ": " ",   // non-breaking space
};
function sanitize(s: string): string {
  let out = s;
  for (const [k, v] of Object.entries(CHAR_MAP)) {
    if (out.includes(k)) out = out.split(k).join(v);
  }
  return out;
}

interface LayoutResult {
  fits: boolean;
  pageCount: number;
  truncatedAt: number | null; // exercise index where we stopped, or null
}

function layoutAttempt(
  doc: jsPDF,
  args: PdfArgs,
  bodyPt: number,
  draw: boolean
): LayoutResult {
  const titlePt = bodyPt + 0.5;        // headings slightly larger
  const lineH = (bodyPt + 1.5) / 72;    // line-height in inches
  const titleH = (titlePt + 1.5) / 72;
  const exerciseGap = 0.06;             // inches between exercises

  let pageNum = 1;
  let col = 0;       // 0 or 1
  let y = MARGIN + HEADER_H;
  const colTop = MARGIN + HEADER_H;
  const colBot = PAGE_H - MARGIN - FOOTER_H;

  function drawHeader() {
    if (!draw) return;
    // Eyebrow
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(ACCENT[0], ACCENT[1], ACCENT[2]);
    doc.text("WORKOUT PLAN", MARGIN, MARGIN + 0.18);
    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`Workout for ${args.date || "—"}`, MARGIN, MARGIN + 0.42);
    // Underline
    doc.setLineWidth(0.012);
    doc.setDrawColor(0, 0, 0);
    doc.line(MARGIN, MARGIN + 0.5, PAGE_W - MARGIN, MARGIN + 0.5);
  }

  function drawColumnRule() {
    if (!draw) return;
    doc.setLineWidth(0.005);
    doc.setDrawColor(220, 220, 220);
    const x = MARGIN + COL_W + COL_GAP / 2;
    doc.line(x, colTop, x, colBot);
  }

  function newPage() {
    pageNum++;
    if (draw) doc.addPage();
    col = 0;
    y = colTop;
    drawHeader();
    drawColumnRule();
  }

  drawHeader();
  drawColumnRule();

  function colX(): number {
    return MARGIN + col * (COL_W + COL_GAP);
  }

  // Estimate height of an exercise. Use sanitised text so wrap counts match
  // what we'll actually render.
  function estimateHeight(ex: PdfExercise): number {
    if (!ex.title && ex.body.length === 0) return 0;
    let h = titleH;
    doc.setFontSize(bodyPt);
    for (const line of ex.body) {
      const wrapped = doc.splitTextToSize(sanitize(line), COL_W) as string[];
      h += wrapped.length * lineH;
    }
    return h + exerciseGap;
  }

  let truncatedAt: number | null = null;

  for (let i = 0; i < args.exercises.length; i++) {
    const ex = args.exercises[i];
    const h = estimateHeight(ex);

    // Place the exercise. If it doesn't fit in the current column, advance.
    if (y + h > colBot) {
      if (col === 0) {
        col = 1;
        y = colTop;
      } else {
        if (pageNum >= MAX_PAGES) {
          truncatedAt = i;
          break;
        }
        newPage();
      }
    }

    // If a single exercise is taller than a column, accept it and let it
    // exceed the column bottom slightly rather than infinite-loop.
    const x = colX();

    if (draw) {
      // Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(titlePt);
      doc.setTextColor(0, 0, 0);
      doc.text(sanitize(ex.title).toUpperCase(), x, y + titleH * 0.85);
    }
    y += titleH;

    if (draw) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(bodyPt);
    }
    for (const line of ex.body) {
      const clean = sanitize(line);
      const wrapped = doc.splitTextToSize(clean, COL_W) as string[];
      for (const w of wrapped) {
        if (draw) doc.text(w, x, y + lineH * 0.85);
        y += lineH;
      }
    }
    y += exerciseGap;
  }

  // Footer on each page.
  if (draw) {
    const totalPages = pageNum;
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      const text = `Created on ${args.generatedOn} • Page ${p} of ${totalPages}`;
      const w = doc.getTextWidth(text);
      doc.text(text, (PAGE_W - w) / 2, PAGE_H - MARGIN + 0.05);
    }
  }

  return {
    fits: truncatedAt === null,
    pageCount: pageNum,
    truncatedAt,
  };
}

export function openWorkoutAsPdf(args: PdfArgs): void {
  // Find the largest font size that fits everything on ≤ MAX_PAGES.
  let chosenSize = MIN_BODY_PT;
  for (const sz of FONT_SIZES) {
    const probe = new jsPDF({ unit: "in", format: "letter" });
    const result = layoutAttempt(probe, args, sz, false);
    if (result.fits && result.pageCount <= MAX_PAGES) {
      chosenSize = sz;
      break;
    }
  }

  // Render for real at the chosen size.
  const doc = new jsPDF({ unit: "in", format: "letter" });
  const final = layoutAttempt(doc, args, chosenSize, true);

  // If even the smallest size couldn't fit, draw a truncation note on the
  // last page so the user knows their plan was clipped.
  if (!final.fits && final.truncatedAt !== null) {
    const remaining = args.exercises.length - final.truncatedAt;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `(${remaining} more selection${remaining === 1 ? "" : "s"} not shown — too many to fit on 2 pages.)`,
      MARGIN,
      PAGE_H - MARGIN - 0.05
    );
  }

  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);

  // Open the blob URL directly in a new tab. Most browsers' built-in PDF
  // viewers will display it inline; a Save/Download button is built into
  // the viewer so the user can save it from there.
  const win = window.open(url, "_blank");
  if (!win) {
    alert(
      "Couldn't open a new tab — your browser may be blocking pop-ups for this site. " +
      "Allow pop-ups and try again."
    );
    URL.revokeObjectURL(url);
    return;
  }

  // Keep the blob URL alive long enough for the new tab to load it, then
  // clean up. 60s is plenty.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
